"use client"
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
  NavigationMenuContent,
} from "@/components/ui/navigation-menu";
import { Logo } from "@/components/ui/logo";
import { getSiteConfig } from '@/lib/config';
import { MobileMenu } from "@/components/ui/mobile-menu";
import { NavLink } from "@/components/ui/nav-link";
import { Phone, Mail } from "lucide-react";
import { SocialWidget } from "@/components/ui/social-widget";
import { ContactIcon } from "@/components/ui/contact-icon";

export function Navbar() {
  const config = getSiteConfig();
  const items = config.navigation.main.filter((item) => item.enabled);
  const styling = config.navigation.styling;
  const { phone, email } = config.company;
  const pathname = usePathname();
  const router = useRouter();

  return (
    <header className="fixed top-0 left-0 w-full z-40 bg-white shadow-sm border-b border-black/5">
      {/* Top Row: Identity & Contact */}
      <div className="container mx-auto px-4 py-3 md:py-4 flex justify-between items-center">
        <Link href="/" className="flex-shrink-0">
          <Logo
            withBackground={false}
            rounded={false}
            height={92}
            width={230}
            containerClassName="flex items-center"
            className="h-16 md:w-[230px] md:h-[92px] w-auto object-contain"
            variant="primary"
          />
        </Link>

        {/* Desktop Contact & Socials */}
        <div className="hidden md:flex items-center gap-3">
          {/* Social Icons - Expandable Widget */}
          <SocialWidget iconColorClass="text-black" />

          {/* Divider */}
          <div className="h-4 w-[1px] bg-gray-300 mx-1" />

          {/* Contact Icons */}
          <div className="flex items-center gap-3">
            {email && (
              <a href={`mailto:${email}`} className="group block" title="Email Us" aria-label="Email Us">
                <div className="bg-black rounded-full p-2 group-hover:bg-[#7db336] transition-colors shadow-sm flex items-center justify-center">
                  <Mail className="h-4 w-4 text-white" />
                </div>
              </a>
            )}
            {phone && (
              <a
                href={`tel:${phone.replace(/[^0-9]/g, "")}`}
                className="flex items-center gap-2 group ml-1"
                title={phone}
              >
                <div className="bg-black rounded-full p-2 group-hover:bg-[#7db336] transition-colors shadow-sm flex items-center justify-center">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <span className="text-black group-hover:text-[#7db336] transition-colors font-medium">
                  {phone}
                </span>
              </a>
            )}
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden">
          <MobileMenu
            items={items.map(({ label, href }) => ({ label, href }))}
            styling={styling}
            overlayColor={config.navigation.mobileBackground ?? config.theme.colors.background}
            enableAnimations={config.motion.enableAnimations}
          />
        </div>
      </div>

      {/* Bottom Row: Navigation Links */}
      <div className="hidden md:block bg-[#391d5a] py-3">
        <div className="container mx-auto px-4">
          <NavigationMenu viewport={false} className="max-w-full justify-start">
            <NavigationMenuList className="flex gap-0 divide-x divide-secondary">
              {items.map((item, index) => (
                <NavigationMenuItem key={item.href} className="px-6 first:pl-0 last:pr-0">
                  {item.items ? (
                    <>
                      <NavigationMenuTrigger
                        onClick={() => router.push(item.href)}
                        className={cn(
                          "bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent data-[state=open]:bg-transparent",
                          "transition-colors text-base font-normal tracking-normal p-0 h-auto cursor-pointer",
                          "text-white hover:text-secondary focus:text-secondary data-[state=open]:text-secondary"
                        )}
                      >
                        <span className={cn(pathname === item.href && "text-secondary font-medium")}>
                          {item.label}
                        </span>
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[300px] gap-1">

                          {item.items.map((subItem) => (
                            <li key={subItem.href}>
                              <NavigationMenuLink asChild>
                                <Link
                                  href={subItem.href}
                                  className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-accent-foreground focus:bg-slate-100 focus:text-accent-foreground text-sm font-medium text-slate-900"
                                >
                                  {subItem.label}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <NavigationMenuLink asChild>
                      <Link
                        href={item.href}
                        className={cn(
                          "transition-colors text-base font-normal tracking-normal bg-transparent",
                          pathname === item.href
                            ? "text-secondary font-medium focus:text-secondary"
                            : "text-white hover:text-secondary focus:text-secondary"
                        )}
                      >
                        {item.label}
                      </Link>
                    </NavigationMenuLink>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>
    </header>
  );
}
